export type NavLink = {
  label: string
  href: string
  /** Nested subcategories, rendered as a flyout (desktop) / indented list (mobile). */
  children?: NavLink[]
}

export type NavGroupId =
  | 'mailboxes'
  | 'commercial'
  | 'street-signs'
  | 'decorative-signage'
  | 'outdoor-accessories'
  | 'lighting'

export type NavGroup = {
  id: NavGroupId
  /** Short label used in the desktop nav bar so the row fits on one line. */
  label: string
  /** Full customer-facing category name used in headings and reports. */
  fullLabel: string
  href: string
  items: NavLink[]
}

const slugify = (label: string) =>
  label
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/["'”’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

/** Legacy-style child route derived from its parent category route. */
const childHref = (parentHref: string, label: string) =>
  `${parentHref.replace(/\.html$/, '')}/${slugify(label)}.html`

/** Build a list of leaf children under a parent route. */
const leaves = (parentHref: string, labels: string[]): NavLink[] =>
  labels.map((label) => ({
    label,
    href: childHref(parentHref, label),
  }))

/**
 * SINGLE SOURCE OF TRUTH for the redesign category plan.
 *
 * Every customer-facing surface that shows category structure — the desktop
 * header dropdowns and nested flyouts, the mobile drawer accordions, the
 * footer category list, the homepage Shop by Need / Popular Paths labels —
 * and the internal /category-cleanup-review report all read this tree. Do not
 * redeclare the category structure anywhere else; edit it here.
 *
 * Notes on the cleaned structure:
 * - "Signs & Addresses" is intentionally NOT a top-level group. It was a broad
 *   nav grouping, not a backend category branch, and is split into Street Signs
 *   & Traffic Signs, Decorative Signage, and Address Plaques & House Numbers
 *   (under Outdoor Accessories & Home Accents).
 * - Multi Mount Mailbox Systems sits directly under Mailboxes.
 * - Door Mats & Welcome Mats is removed from all customer-facing structure.
 * - Review / Unassigned is internal-only and never appears here.
 * - Level-2/3 routes follow the legacy `/6m8/<parent>/<slug>.html` pattern and
 *   are registered in data/categoryRegistry.ts so they render a real category
 *   page instead of the generic fallback.
 */
export const PRODUCT_NAV: NavGroup[] = [
  {
    id: 'mailboxes',
    label: 'Mailboxes',
    fullLabel: 'Mailboxes',
    href: '/6m8/mailboxes.html',
    items: [
      {
        label: 'Curbside Mailboxes',
        href: '/6m8/curbside-mailboxes.html',
        children: leaves('/6m8/curbside-mailboxes.html', [
          'Bright Lite Solutions',
          'Fulton Mailbox Corp',
          'Special Lite Products',
          'Gaines Keystone Mailboxes',
          'Janzer Curbside Mailboxes',
          'Salsbury Rural Mailboxes',
          'Whitehall Mailboxes',
          'Bacova Bluegrass Woods',
          'Cedar Post Mount Mailboxes',
          'Americas Finest Post Mount Mailboxes',
        ]),
      },
      {
        label: 'Complete Mailbox & Post Systems',
        href: '/6m8/mailboxes/complete-mailbox-post-system.html',
        children: leaves('/6m8/mailboxes/complete-mailbox-post-system.html', [
          'BrightLight',
          'Imperial Mailbox Systems',
          'The Williamsburg Complete Mailbox Systems',
          'Premiere Systems',
          'Gaines Mfg',
          'Janzer',
          'Mayne Post Systems',
          'Special Lite Products',
          'Whitehall Systems',
          'Barcelona Systems',
          'Qualarc Systems',
        ]),
      },
      {
        label: 'Multi Mount Mailbox Systems',
        href: '/6m8/mailboxes/multi-mount-mailbox-systems.html',
        children: leaves('/6m8/mailboxes/multi-mount-mailbox-systems.html', [
          'Whitehall',
          'Gaines Mfg',
          'Janzer',
          'Imperial',
          'Premier',
          'Barcelona',
          'Mayne',
        ]),
      },
      {
        label: 'Wall Mount Mailboxes',
        href: '/6m8/mailboxes/wall-mount-mailboxes.html',
        children: leaves('/6m8/mailboxes/wall-mount-mailboxes.html', [
          'Americas Finest',
          'Gaines Mfg',
          'Special Lite',
          'Winfield',
          'Whitehall',
          'Bacova Wall Mount Mailboxes',
        ]),
      },
      {
        label: 'Mailbox Posts',
        href: '/6m8/mailbox-posts.html',
        children: leaves('/6m8/mailbox-posts.html', [
          'Mailboss Post',
          'Royal Crowne Mailbox Posts',
          'Mayne Posts',
          'Special Lite Products Mailbox Post',
          'Gaines Mailbox Post',
          'Cedar Mailbox Posts',
          'Whitehall Mailbox Posts',
          'Handy Post',
        ]),
      },
      {
        label: 'Locking Mailboxes',
        href: '/6m8/mailboxes/locking-mailboxes.html',
        children: leaves('/6m8/mailboxes/locking-mailboxes.html', [
          'Mailboss',
          'Qualarc',
          'Salsbury',
        ]),
      },
      {
        label: 'Stone & Brick Column Mailboxes',
        href: '/6m8/mailboxes/stone-brick-column-mailboxes.html',
      },
      {
        label: 'Mailbox Parts & Accessories',
        href: '/6m8/mailboxes/mailbox-replacement-parts-and-accessories.html',
        children: [
          ...leaves(
            '/6m8/mailboxes/mailbox-replacement-parts-and-accessories.html',
            [
              'Replacement Mailbox Post Brackets',
              'Locking Inserts',
              'Decorative Post Bases',
              'Post Finals',
              'Whitehall Mailbox Replacement Plaques',
            ],
          ),
          {
            label: 'Replacement Numbers',
            href: '/6m8/mailboxes/replacement-numbers.html',
          },
        ],
      },
    ],
  },
  {
    id: 'commercial',
    label: 'Commercial',
    fullLabel: 'Commercial Mailboxes & Parcel Lockers',
    href: '/6m8/commercial-mailboxes-lockers.html',
    items: [
      {
        label: 'Centralized Delivery Mailboxes & Kiosks',
        href: '/6m8/commercial-mailboxes-lockers/centralized-delivery-mailboxes-and-kiosks.html',
        children: leaves(
          '/6m8/commercial-mailboxes-lockers/centralized-delivery-mailboxes-and-kiosks.html',
          [
            'Standard USPS Approved Cluster Box Mailbox',
            'Decorative USPS Approved Cluster Box Mailbox',
            'Parcel Lockers-USPS Approved Cluster Mailbox',
            '4C Horizontal USPS Approved Recessed & Surface Mount',
            '4C16 - Max Height',
            '4C15',
            '4C14',
            '4C13',
            '4C12',
            '4C11',
            '4C-ADA',
            '4C10',
            '4C09',
            '4C08',
            '4C07',
            '4C06',
            '4C Horizontal Mailbox Depot Packages',
            '4B+ Horizontal Mailboxes - USPS Approval Required',
            'Vertical Mailboxes - USPS Approval Required',
          ],
        ),
      },
      {
        label: 'Private Delivery Mailboxes',
        href: '/6m8/commercial-mailboxes-lockers/private-delivery-mailboxes.html',
      },
      {
        label: 'Parcel Lockers',
        href: '/6m8/commercial-mailboxes-lockers/parcel-lockers.html',
      },
      {
        label: 'Mail Slots, Letter & Book Drops',
        href: '/6m8/commercial-mailboxes-lockers/mail-slots-letter-book-drops.html',
      },
      {
        label: 'Collection Boxes',
        href: '/6m8/commercial-mailboxes-lockers/collection-boxes.html',
      },
      {
        label: 'Key Keepers & Key Blanks',
        href: '/6m8/commercial-mailboxes-lockers/key-keepers-key-blanks.html',
      },
      {
        label: 'Commercial Replacement Parts',
        href: '/6m8/commercial-mailboxes-lockers/replacement-commercial-locks-hinges-doors.html',
      },
    ],
  },
  {
    id: 'street-signs',
    label: 'Street Signs & Traffic Signs',
    fullLabel: 'Street Signs & Traffic Signs',
    href: '/6m8/street-signs-traffic-signs.html',
    items: [
      {
        label: 'Traffic & Road Signs',
        href: '/6m8/street-signs-traffic-signs/traffic-road-signs.html',
      },
      {
        label: 'Sign Frames & Back Plates',
        href: '/6m8/street-signs-traffic-signs/sign-frames-back-plates.html',
      },
      {
        label: 'Replacement Bases, Finials & Sign Mounts',
        href: '/6m8/decorative-signage/replacement-bases-finials-sign-mounts.html',
      },
      {
        label: 'Message Boards',
        href: '/6m8/message-boards.html',
      },
    ],
  },
  {
    id: 'decorative-signage',
    label: 'Decorative Signage',
    fullLabel: 'Decorative Signage',
    href: '/6m8/decorative-signage.html',
    items: [
      {
        label: 'Decorative Street Sign',
        href: '/6m8/decorative-signage/decorative-street-sign.html',
        children: leaves(
          '/6m8/decorative-signage/decorative-street-sign.html',
          [
            'Premier 3" Street Sign Program',
            'Special Lite Street Sign Programs',
            'Executive 4" Streeet Sign Program',
            'Imperial Mailbox Systems Street Signs',
          ],
        ),
      },
      {
        label: 'Decorative Stop Signs',
        href: '/6m8/decorative-signage/decorative-stop-signs.html',
        children: leaves('/6m8/decorative-signage/decorative-stop-signs.html', [
          'Premiere 3" Stop Signs',
          'Special Lite Stop Sign Programs',
          'Executive 4" Stop Signs',
          'Imperial Mailbox Systems Stop Signs',
        ]),
      },
      {
        label: 'Decorative Traffic Signage',
        href: '/6m8/decorative-signage/decorative-traffic-signage.html',
        children: leaves(
          '/6m8/decorative-signage/decorative-traffic-signage.html',
          [
            'Preimiere 3" Traffic Signs',
            'Special Lite Products Traffic Sign Programs',
            'Executive 4" Traffic Signs',
            'Imperial Mailbox Systems Traffic Signs',
          ],
        ),
      },
    ],
  },
  {
    id: 'outdoor-accessories',
    label: 'Outdoor Accessories',
    fullLabel: 'Outdoor Accessories & Home Accents',
    href: '/6m8/outdoor-accessories-home-accents.html',
    items: [
      {
        label: 'Address Plaques & House Numbers',
        href: '/6m8/address-plaques-and-house-numbers.html',
        children: [
          ...leaves('/6m8/address-plaques-and-house-numbers.html', [
            'Montague House Numbers',
            'Whitehall Cast Alum Stand Off House Numbers',
            'Hillman Polished Brass House Numbers',
            'Americas Finest House Numbers',
            'Montague Plaques',
            'Whitehall Plaques & Numbers',
            'Whitehall America 250TH Wall Plaques',
            'Gaines Address Plaques',
            'Majestic Plaques',
            'Qualarc Address Plaques',
          ]),
          {
            label: 'Illuminated Address Plaques',
            href: '/6m8/address-plaques-and-house-numbers/illuminated-address-plaques.html',
          },
        ],
      },
      {
        label: 'Home Exterior Accents',
        href: '/6m8/outdoor-accessories-home-accents/home-exterior-accents.html',
        children: [
          {
            label: 'Garden Accents',
            href: '/6m8/outdoor-accessories-home-accents/home-exterior-accents/garden-accents.html',
            children: leaves(
              '/6m8/outdoor-accessories-home-accents/home-exterior-accents/garden-accents.html',
              ['Hose Holders', 'Garden Signs'],
            ),
          },
          ...leaves(
            '/6m8/outdoor-accessories-home-accents/home-exterior-accents.html',
            [
              'Towel Valets',
              'Country Bells',
              'Weathervanes',
              'Personalized Ceramic Crocks & Planters',
              'Decorative Rain Catchers & Storage Bins',
            ],
          ),
        ],
      },
      {
        label: 'Planters',
        href: '/6m8/home-garden-accents/planters.html',
      },
      {
        label: 'Window Box Planters',
        href: '/6m8/home-garden-accents/window-box-planters.html',
        children: leaves('/6m8/home-garden-accents/window-box-planters.html', [
          'Mayne Yorkshire Window Box Planter',
          'Mayne Fairfield Window Box Planter',
          'Mayne Nantucket Window Box Planter',
          'Mayne Cape Cod Window Boxes',
          'Mayne Window Box Replacement Parts',
        ]),
      },
      {
        label: 'Fairfield 50QT Coolers',
        href: '/6m8/home-garden-accents/fairfield-50qt-coolers.html',
      },
      {
        label: 'Door Chimes & Doorbells',
        href: '/6m8/mailboxes/door-chimes-doorbells.html',
      },
    ],
  },
  {
    id: 'lighting',
    label: 'Lighting',
    fullLabel: 'Outdoor Lighting',
    href: '/6m8/lighting.html',
    items: [
      {
        label: 'Outdoor Lamp Posts',
        href: '/6m8/lighting/outdoor-lamp-posts.html',
        children: leaves('/6m8/lighting/outdoor-lamp-posts.html', [
          'Solar Lighting',
          'Mayne Vinyl Lamp Posts',
          'Special Lite Surface Mounted Lamp Posts',
          'Special Lite Direct Burial Lamp Posts',
          'Hanover Lantern Surface Mounted Lamp Posts',
          'Hanover Lantern Direct Burial Lamp Posts',
          'Light Post Accessories',
        ]),
      },
      {
        label: 'Outdoor Exterior Lighting',
        href: '/6m8/lighting/outdoor-exterior-lighting.html',
      },
      {
        label: 'Exterior Home Lighting',
        href: '/6m8/lighting/exterior-home-lighting.html',
      },
      {
        label: 'Commercial Lighting',
        href: '/6m8/lighting/commercial-lighting.html',
      },
      {
        label: 'Light Blocks',
        href: '/6m8/lighting/light-blocks.html',
      },
    ],
  },
]

/** Look up a top-level group by id so components never re-declare the tree. */
export function getNavGroup(id: NavGroupId): NavGroup {
  const group = PRODUCT_NAV.find((g) => g.id === id)
  if (!group) throw new Error(`Unknown product nav group: ${id}`)
  return group
}

/** Look up a first-level subcategory inside a group by its exact label. */
export function getNavItem(id: NavGroupId, label: string): NavLink {
  const item = getNavGroup(id).items.find((i) => i.label === label)
  if (!item) throw new Error(`Unknown ${id} subcategory: ${label}`)
  return item
}

export type FlatNavRoute = {
  route: string
  label: string
  groupId: NavGroupId
  /** Route of the closest ancestor category, used for data/SEO inheritance. */
  parentRoute: string
  depth: number
}

/** Depth-first list of every category route in the tree (levels 1-3). */
export function flattenNavRoutes(): FlatNavRoute[] {
  const out: FlatNavRoute[] = []
  const walk = (
    items: NavLink[],
    groupId: NavGroupId,
    parentRoute: string,
    depth: number,
  ) => {
    for (const item of items) {
      out.push({
        route: item.href,
        label: item.label,
        groupId,
        parentRoute,
        depth,
      })
      if (item.children) walk(item.children, groupId, item.href, depth + 1)
    }
  }
  for (const group of PRODUCT_NAV) {
    out.push({
      route: group.href,
      label: group.fullLabel,
      groupId: group.id,
      parentRoute: group.href,
      depth: 0,
    })
    walk(group.items, group.id, group.href, 1)
  }
  return out
}
